import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormLabel,
    Paper,
    Radio,
    RadioGroup,
    Stack,
    Typography
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { TextField } from '../../components/input';
import {
    PreferencePicker,
    formatPreferenceText,
    isPreferenceUsable,
    normalizePreferenceSettings,
    requiredSelections,
    type PreferencePick
} from '../../modules/cart-apptpref';
import {
    AvailabilityPicker,
    CALL_TO_SCHEDULE,
    availabilityQueryKeys,
    buildWeekMask,
    resolveSlotMember,
    type AvailabilitySelection
} from '../../modules/cart-gcal';
import {
    ANY_AVAILABLE_LABEL,
    TeamPicker,
    buildTeamCards,
    defaultSelection,
    teamCalendarMemberIds,
    type TeamSelection
} from '../../modules/cart-team';
import {
    unitsForCountry,
    isPhoneScreen,
    useCalendarWeather,
    type CalendarWeather,
    type WeatherRequest
} from '../../modules/cart-weather';
import { DroneAirspaceNotice, useDroneAirspace } from '../../modules/cart-drone';
import { useHost } from '../../hooks/use-host';
import { useSessionOptional } from '../../contexts/session-context';
import { brandHostHeaders, getCheckoutPass } from '../../util/checkout-pass';
import { useSlotHold } from '../../modules/cart-gcal';
import {
    fetchRescheduleAppointments,
    movingTaskIdsOf,
    rescheduleErrorStatus,
    useRescheduleSave,
    useRescheduleTask,
    useRescheduleTeam,
    type RescheduleCaller,
    type RescheduleGroup,
    type RescheduleRequest,
    type RescheduleTask,
    type RescheduleTeamMember
} from '../../hooks/use-reschedule';
import { getMediaSize } from '../../util/media';
import { T } from '../../util/Localize';
import {
    brandToday,
    formatAppointmentDate,
    formatAppointmentTime,
    formatCountdown,
    formatSiteAddress,
    teamPhotoUrl,
    toShootDate
} from './reschedule-format';

/**
 * The reschedule page (ROBO-616) — legacy `Dashboard/reschedule.asp` — and, since ROBO-694, its
 * "Check More Photographers": another photographer, "Any Available", the hidden-team calendar and
 * the travel fee that goes with the choice.
 *
 * ★ THE PAGE IS A HOST, NOT THE OWNER, OF THE CHOOSER. The cards and their rules are
 * `src/modules/cart-team`; the calendar, the slot hold and the "which member gets this slot" rule
 * are `src/modules/cart-gcal`. This file only feeds them the team route's answer and carries the
 * result into the hold and the save. Who is OFFERED is the server's decision, never this page's.
 * A preference brand's slot calendar (ROBO-698) is `src/modules/cart-apptpref` in the same way:
 * the page hands it the task's `preference` settings and saves the text its rules produce.
 *
 * It serves everyone legacy serves and assumes nothing about which: a guest holding the site's
 * link, a client, staff, and the mobile app's webview. Who is asking is decided by the caller
 * (`hub-reschedule-page.tsx`) and arrives as `downloadGuid` (a guest) or nothing (a session); what
 * they may do arrives from the API as flags (`tooLate`, `canTbd`, `canCancel`, …), never from a
 * role check here.
 *
 * Copy is legacy's, word for word, wherever legacy had any (`reschedule.asp` :523-700).
 */

export const NOT_FOUND_MESSAGE = 'No schedulable appointment was found.';
export const CONFLICT_MESSAGE = 'That time is no longer available. Please choose another time.';
export const HOLD_REFUSED_MESSAGE = 'That time was just taken. Please choose another time.';
export const HOLD_EXPIRED_MESSAGE =
    'Your held appointment time has expired. Please choose a time again.';
const SAVE_FAILED_MESSAGE = 'We could not save your changes. Please try again.';
const HOLD_FAILED_MESSAGE = 'We could not hold that time. Please try again.';
/** Legacy's button, word for word (`reschedule.asp` :622). */
export const CHECK_MORE_LABEL = 'Check More Photographers';
export const CHOOSE_PHOTOGRAPHER_MESSAGE = 'Select a photographer to see available times.';
const TEAM_FAILED_MESSAGE = 'We could not load the photographers. Please try again.';

const NO_MEMBERS: readonly RescheduleTeamMember[] = [];

/**
 * Whose calendar is on screen:
 * - `member`  one photographer's own calendar (the current one, or a chosen card);
 * - `team`    ONE merged calendar over several — "Any Available" (`isAny`) or the hidden-team
 *             calendar. Nobody is chosen until a slot is picked; the slot then resolves to one;
 * - `choose`  the cards are showing and none is selected — legacy shows no calendar either;
 * - `empty`   the cards are showing and there is nobody to offer;
 * - `none`    nobody to schedule with: "Please call to schedule."
 */
type CalendarTarget =
    | { kind: 'member'; memberId: number; name: string | null }
    | { kind: 'team'; memberIds: number[]; isAny: boolean }
    | { kind: 'choose' }
    | { kind: 'empty' }
    | { kind: 'none' };

function targetKey(target: CalendarTarget): string {
    if (target.kind === 'member') return `member:${target.memberId}`;
    if (target.kind === 'team') {
        return `team:${target.isAny ? 'any' : 'hidden'}:${target.memberIds.join('_')}`;
    }
    return target.kind;
}

type Mode = 'schedule' | 'tbd' | 'cancel';

interface Completed {
    shootDate: string | null;
    /** A whole-day pick: there is no time of day to show. */
    isDateOnly: boolean;
}

interface NativeWebView {
    postMessage: (message: string) => void;
}

/** The mobile app's webview bridge, when this page is running inside it. */
function nativeWebView(): NativeWebView | undefined {
    return (window as unknown as { ReactNativeWebView?: NativeWebView }).ReactNativeWebView;
}

const slotKey = (date: string, minute: number) => `${date}|${minute}`;

export interface RescheduleProps {
    orderId: number;
    taskId: number;
    /** The site's default download guid — set for a GUEST only (no session). */
    downloadGuid?: string;
    /**
     * Where "Cancel Changes" / "Back to Dashboard" goes. Set for a signed-in user only: legacy
     * offers a guest no way out, because a guest has nowhere to go back to.
     */
    exitPath?: string;
}

export default function Reschedule({ orderId, taskId, downloadGuid, exitPath }: RescheduleProps) {
    const caller = useMemo<RescheduleCaller>(() => ({ downloadGuid }), [downloadGuid]);
    const isValidRoute = orderId > 0 && taskId > 0;
    const taskQuery = useRescheduleTask(orderId, taskId, caller, isValidRoute);
    const task = taskQuery.data;

    const [completed, setCompleted] = useState<Completed | null>(null);

    const handleExit = useCallback(() => {
        const app = nativeWebView();
        if (app) {
            // Legacy `hd.appMessage('close')` (`reschedule.asp` :634).
            app.postMessage('close');
            return;
        }
        if (exitPath) window.location.assign(exitPath);
    }, [exitPath]);

    const exitButton = (label: string) =>
        exitPath ? (
            <Button variant="outlined" onClick={handleExit}>
                {T(label)}
            </Button>
        ) : null;

    if (!isValidRoute) {
        return <RescheduleNotFound />;
    }

    if (taskQuery.isPending) {
        return (
            <RescheduleFrame>
                <Stack role="status" alignItems="center" spacing={2} sx={{ py: 6 }}>
                    <CircularProgress size={28} />
                    <Typography variant="body2">{T('Loading your appointment...')}</Typography>
                </Stack>
            </RescheduleFrame>
        );
    }

    if (taskQuery.isError || !task) {
        // ★ Uniform on purpose: a wrong id, someone else's appointment, a finished one and a bad
        // link all read the same, because the API answers them all with the same 404.
        const isNotFound = rescheduleErrorStatus(taskQuery.error) === 404;
        return (
            <RescheduleFrame>
                {isNotFound ? (
                    <Alert severity="info">{T(NOT_FOUND_MESSAGE)}</Alert>
                ) : (
                    <Alert
                        severity="error"
                        action={
                            <Button
                                color="inherit"
                                size="small"
                                onClick={() => void taskQuery.refetch()}
                            >
                                {T('Try again')}
                            </Button>
                        }
                    >
                        {T('We could not load this appointment. Please try again.')}
                    </Alert>
                )}
            </RescheduleFrame>
        );
    }

    if (completed) {
        return (
            <RescheduleFrame>
                <CurrentAppointment
                    task={task}
                    shootDate={completed.shootDate}
                    isDateOnly={completed.isDateOnly}
                />
                <StateMessage
                    title="Update Complete"
                    body="Your appointment has been updated."
                    severity="success"
                    shouldFocus
                />
                {exitButton('Back to Dashboard')}
            </RescheduleFrame>
        );
    }

    if (task.zeroTime) {
        // Legacy answers a zero-time task exactly as it answers an unknown one (its read has
        // `HAVING sum(time) != 0`): the same generic state, with nothing about why.
        return <RescheduleNotFound />;
    }

    if (task.tooLate) {
        return (
            <RescheduleFrame>
                <CurrentAppointment task={task} shootDate={task.shootDate} />
                <StateMessage
                    title="Too Late to Reschedule"
                    body="Your window to reschedule this appointment has passed."
                    severity="warning"
                />
                {exitButton('Back to Dashboard')}
            </RescheduleFrame>
        );
    }

    return (
        <RescheduleFrame>
            <RescheduleForm
                task={task}
                orderId={orderId}
                taskId={taskId}
                caller={caller}
                onCompleted={setCompleted}
                cancelButton={exitButton('Cancel Changes')}
            />
        </RescheduleFrame>
    );
}

/**
 * The one "cannot be shown" state. Uniform on purpose: a wrong id, someone else's appointment, a
 * finished or zero-time one, a bad link and a visitor with neither a session nor a link all read
 * the same. Makes no request, so a caller with nothing to identify them reads nothing.
 */
export function RescheduleNotFound() {
    return (
        <RescheduleFrame>
            <Alert severity="info">{T(NOT_FOUND_MESSAGE)}</Alert>
        </RescheduleFrame>
    );
}

interface RescheduleFormProps {
    task: RescheduleTask;
    orderId: number;
    taskId: number;
    caller: RescheduleCaller;
    onCompleted: (completed: Completed) => void;
    cancelButton: ReactNode;
}

function RescheduleForm({
    task,
    orderId,
    taskId,
    caller,
    onCompleted,
    cancelButton
}: RescheduleFormProps) {
    const queryClient = useQueryClient();
    const { data: hostInfo } = useHost();
    const save = useRescheduleSave(orderId, taskId, caller);
    // Asked only when the task says the caller may choose (ROBO-694 contract B).
    const teamQuery = useRescheduleTeam(orderId, taskId, caller, task.canChoosePhotographer);
    const team = teamQuery.data;
    const modeLabelId = useId();
    const chooserHeadingRef = useRef<HTMLHeadingElement>(null);

    const hasCalendar = task.calendarModule === 'cart-gcal';
    const hasPreferences = task.calendarModule === 'cart-apptpref';
    // Legacy hides "Schedule Now" when the brand's cart has neither module (`reschedule.asp` :616).
    const canSchedule = hasCalendar || hasPreferences;
    const hasAppointment = task.shootDate !== null;

    const modes = useMemo<Mode[]>(
        () => [
            ...(canSchedule ? (['schedule'] as const) : []),
            ...(task.canTbd ? (['tbd'] as const) : []),
            ...(task.canCancel ? (['cancel'] as const) : [])
        ],
        [canSchedule, task.canCancel, task.canTbd]
    );
    const [mode, setMode] = useState<Mode | undefined>(modes[0]);

    const [selection, setSelection] = useState<AvailabilitySelection | null>(null);
    /** Team calendar only: the ONE member the picked slot resolved to — who is held and saved. */
    const [resolvedMemberId, setResolvedMemberId] = useState<number | null>(null);
    const [pending, setPending] = useState<AvailabilitySelection | null>(null);
    const [isChooserOpen, setIsChooserOpen] = useState(false);
    /** The card the user picked. `undefined` until they pick: the default rule decides till then. */
    const [userChoice, setUserChoice] = useState<TeamSelection | undefined>(undefined);
    const [isShowingAll, setIsShowingAll] = useState(false);
    const [preferences, setPreferences] = useState('');
    /** The slot calendar's picks, in pick order (ROBO-698). */
    const [picks, setPicks] = useState<PreferencePick[]>([]);
    const [comment, setComment] = useState('');
    const [message, setMessage] = useState<string | null>(null);
    /** Slots known to be gone: refused holds, and a day's open list as the hold route reported it. */
    const [refused, setRefused] = useState<ReadonlySet<string>>(new Set());
    const [openByDate, setOpenByDate] = useState<Record<string, number[]>>({});

    const refreshAvailability = useCallback(
        () => queryClient.invalidateQueries({ queryKey: availabilityQueryKeys.all }),
        [queryClient]
    );

    const slotHold = useSlotHold({
        downloadGuid: caller.downloadGuid,
        onExpired: () => {
            // Legacy `expireUpdate` (`cart-gcal.js` :454-468): the selection goes with the hold.
            setSelection(null);
            setMessage(HOLD_EXPIRED_MESSAGE);
            void refreshAvailability();
        }
    });

    /*
     * ── The preference calendar (ROBO-698) ─────────────────────────────────────────────────────
     * Legacy `cart-apptpref` as `reschedule.asp` drives it (:351-363). "Today" is the ORDER
     * brand's date, from the API. Team days off and the service-area week mask are not passed:
     * legacy does not apply either on this page (`cart-apptpref.js` :21, :177 need cart state).
     *
     * ★ NO SLOTS, NO CALENDAR. A design that names no slots — and an API older than the ROBO-698
     *   slice, which sends no `preference` at all — keeps the free-text input. Legacy would draw
     *   a grid nobody can pick from, and its Save could never enable.
     */
    const preference = task.preference;
    const preferenceSettings = useMemo(
        () => (hasPreferences && preference ? normalizePreferenceSettings(preference) : null),
        [hasPreferences, preference]
    );
    const slotCalendar =
        preferenceSettings && isPreferenceUsable(preferenceSettings) ? preferenceSettings : null;

    const latitude = task.site.latitude ?? null;
    const longitude = task.site.longitude ?? null;
    const location = useMemo(
        () => (latitude !== null && longitude !== null ? { latitude, longitude } : null),
        [latitude, longitude]
    );
    // The whole group that moves, not just the task in the address: each of them is "busy" at the
    // old time, and both availability and the hold must look past all of them.
    const movingKey = movingTaskIdsOf(task).join(',');
    const ignoreTaskIds = useMemo(() => movingKey.split(',').map(Number), [movingKey]);

    /*
     * ── The photographer chooser (ROBO-694) ────────────────────────────────────────────────────
     * Legacy `changeCalendar()` (`reschedule.asp` :406-426) plus its auto-open (:454-462): when the
     * current photographer cannot do the product the chooser is open from the start, for every
     * caller kind, and there is no button.
     */
    const members = team?.members ?? NO_MEMBERS;
    const chooser = hasCalendar ? (team?.chooser ?? 'none') : 'none';
    const isAutoOpen = task.autoOpenPhotographers === true || team?.autoOpen === true;
    const isChooserActive = chooser !== 'none' && (isChooserOpen || isAutoOpen);
    const isTeamPending = task.canChoosePhotographer && teamQuery.isPending;

    const cards = useMemo(
        () =>
            buildTeamCards(members, {
                anyAvailable: team?.anyAvailable ?? false,
                displayLimit: team?.displayLimit,
                isShowingAll
            }),
        [isShowingAll, members, team?.anyAvailable, team?.displayLimit]
    );

    const isOnScreen = (value: TeamSelection) =>
        value === 'any' ? cards.hasAnyCard : cards.members.some(member => member.id === value);
    // Legacy `cartTeam.update` (:393-407): keep a visible choice, else the default rule. On the
    // auto-open the first card is taken when no rule picks one (`reschedule.asp` :457-460).
    const choice: TeamSelection =
        userChoice !== undefined && userChoice !== null && isOnScreen(userChoice)
            ? userChoice
            : defaultSelection({
                  members: cards.members,
                  hasAnyCard: cards.hasAnyCard,
                  current: task.teamMemberId,
                  fallbackToFirst: isAutoOpen
              });

    const target = ((): CalendarTarget => {
        const canTeam = team?.teamMode === 1 || team?.teamMode === 2;
        const teamOf = (ids: number[], isAny: boolean): CalendarTarget =>
            canTeam && ids.length > 0 ? { kind: 'team', memberIds: ids, isAny } : { kind: 'none' };
        const current: CalendarTarget =
            task.teamMemberId === null
                ? { kind: 'none' }
                : {
                      kind: 'member',
                      memberId: task.teamMemberId,
                      name: task.teamMemberName ?? null
                  };

        if (!isChooserActive) return current;
        // Hidden team (`cart-team.mode` 2): one unnamed calendar over everyone offered.
        if (chooser === 'team-calendar') return teamOf(teamCalendarMemberIds(members), false);
        // Nobody to offer. On the auto-open the current photographer cannot do the job, so there
        // is no calendar at all; when the user merely asked to see more, theirs stays.
        if (cards.all.length === 0) return isAutoOpen ? { kind: 'empty' } : current;
        if (choice === null) return { kind: 'choose' };
        // "Any Available" searches only the cards on screen (`cart-gcal.js` :44-46).
        if (choice === 'any') return teamOf(teamCalendarMemberIds(cards.members), true);
        return {
            kind: 'member',
            memberId: choice,
            name: members.find(member => member.id === choice)?.name ?? null
        };
    })();
    const calendarKey = targetKey(target);

    // A different calendar is a different question: the pick made on the old one goes, as legacy's
    // rebuild drops it (`cart-gcal.js` :60 `reserveClear`). State is reset while rendering (no
    // flash of a stale selection); the hold itself is given back in the effect below.
    const [pickedOnKey, setPickedOnKey] = useState(calendarKey);
    if (pickedOnKey !== calendarKey) {
        setPickedOnKey(calendarKey);
        setSelection(null);
        setResolvedMemberId(null);
        setRefused(new Set());
        setOpenByDate({});
    }
    const releaseHold = slotHold.release;
    const heldOnKey = useRef(calendarKey);
    useEffect(() => {
        if (heldOnKey.current === calendarKey) return;
        heldOnKey.current = calendarKey;
        void releaseHold();
    }, [calendarKey, releaseHold]);

    // Service-area data the calendar and the slot resolver need, straight from the team route.
    const weekMask = useMemo(() => buildWeekMask(members), [members]);
    const favoriteIds = useMemo(
        () => members.filter(member => member.favorite).map(member => member.id),
        [members]
    );

    /*
     * ── Weather on the calendars (ROBO-846) ─────────────────────────────────────────────────────
     * A hint, never a gate: the calendar draws exactly as it would without it, and the forecast is
     * asked for only once a calendar is on screen — beside the availability read, never before it.
     * - on by default; the ORDER's calendar module opts out with `disableweather`, which the task reports
     *   as `weatherDisabled` (ROBO-865) — the same answer for a guest and for staff on another brand's order;
     * - a signed-in caller calls with its session; a guest (`downloadGuid`) with no session and a
     *   checkout pass (`getCheckoutPass`, the verify.hd.pics Turnstile frame) plus the brand host. A
     *   pass that cannot be had — a challenge wanting the visitor, a failure — is no weather;
     * - units follow the host brand's country (`hostInfo.country`), °F/mph for the US;
     * - none on a phone (Herb, 2026-10-29): no forecast and no pass request, as on the legacy cart.
     *   A phone is the device's screen under 640 px (`isPhoneScreen`, the legacy `base.isPhone`);
     *   a narrowed desktop window still gets weather;
     * - one request per rounded location per page load (`useCalendarWeather`); any failure, a
     *   401 or a 429 included, is no `weather` prop and nothing on screen.
     */
    const session = useSessionOptional();
    const isPhone = useMemo(() => isPhoneScreen(), []);
    const isWeatherOn =
        !isPhone && !!task && !task.weatherDisabled && (!!caller.downloadGuid || !!session?.user);
    const weatherRequest = useMemo<WeatherRequest | undefined>(
        () =>
            caller.downloadGuid
                ? {
                      skipAuth: true,
                      headers: async () => ({
                          ...brandHostHeaders(),
                          'hd-pass': await getCheckoutPass()
                      })
                  }
                : undefined,
        [caller.downloadGuid]
    );
    const isPreferenceShown = mode === 'schedule' && slotCalendar !== null;
    const isAvailabilityShown =
        mode === 'schedule' &&
        hasCalendar &&
        !isTeamPending &&
        (target.kind === 'member' || target.kind === 'team');
    const weather: CalendarWeather | undefined = useCalendarWeather({
        enabled: isWeatherOn && (isPreferenceShown || isAvailabilityShown),
        location,
        units: unitsForCountry(hostInfo?.country),
        precipThreshold: task?.weatherPrecipThreshold,
        request: weatherRequest
    });

    /*
     * ── Drone airspace (ROBO-854) ───────────────────────────────────────────────────────────────
     * When drone products move with the appointment, the FAA airspace for each picked date (or,
     * before one is picked, undated), with `noFly` from the API's placement rule. A notice only:
     * the save is never blocked by it, and any failure says "could not check". Gated like the
     * weather: a guest has no checkout pass, so it is never sent.
     */
    const pickedDates =
        mode !== 'schedule' ? [] : selection ? [selection.date] : picks.map(pick => pick.date);
    const droneNoticeValue = useDroneAirspace({
        enabled: !caller.downloadGuid && !!session?.user && mode !== 'cancel',
        location,
        droneProducts: task.droneProducts ?? [],
        shootDates: pickedDates
    });

    const handleOpenChooser = () => {
        setMessage(null);
        setIsChooserOpen(true);
        // The control that was pressed is about to disappear; put focus where the result is.
        setTimeout(() => chooserHeadingRef.current?.focus(), 0);
    };

    const handleChoice = (value: TeamSelection) => {
        setMessage(null);
        setUserChoice(value);
    };

    const handleShowAll = () => {
        // Keep what is selected now: more cards on screen must not move the default.
        setUserChoice(choice);
        setIsShowingAll(true);
    };

    const isSlotDisabled = useCallback(
        (date: string, minute: number) => {
            if (refused.has(slotKey(date, minute))) return true;
            const open = openByDate[date];
            return open !== undefined && !open.includes(minute);
        },
        [openByDate, refused]
    );

    const markRefused = (date: string, minute: number) =>
        setRefused(current => new Set(current).add(slotKey(date, minute)));

    /**
     * Who a pick is for. One member's calendar: that member. A team calendar: legacy `slotClick`
     * (`cart-gcal.js` :797-857) — the slot's candidates, narrowed by the customer's favourites,
     * the brand's `teamselect` rule and the rank, then one at random among equals.
     */
    const memberFor = (picked: AvailabilitySelection): number | null => {
        if (target.kind === 'member') return target.memberId;
        if (target.kind !== 'team') return null;
        const resolved = resolveSlotMember(picked.candidates ?? [], {
            favoriteIds,
            teamSelect: team?.teamSelect,
            openOrders: picked.openOrders,
            priorities: Object.fromEntries(members.map(member => [member.id, member.priority]))
        });
        // A calendar over exactly one member may answer in the single-member shape (no names).
        return resolved ?? (target.memberIds.length === 1 ? target.memberIds[0] : null);
    };

    const handleSelect = async (picked: AvailabilitySelection) => {
        setMessage(null);
        const isTeamCalendar = target.kind === 'team';
        const memberId = memberFor(picked);

        if (isTeamCalendar && memberId === null) {
            // The slot named nobody: there is no one to hold it for, and nothing to save.
            void slotHold.release();
            setSelection(null);
            setResolvedMemberId(null);
            setMessage(HOLD_FAILED_MESSAGE);
            void refreshAvailability();
            return;
        }

        // A whole-day pick has no slot to hold (`cart-gcal.js` :865-867), and without coordinates
        // the hold route cannot be asked. The save still re-checks the time on the server.
        if (picked.minute === null || !location || memberId === null) {
            void slotHold.release();
            setSelection(picked);
            setResolvedMemberId(isTeamCalendar ? memberId : null);
            return;
        }

        setSelection(null);
        setResolvedMemberId(null);
        setPending(picked);
        // ONE hold, for the one member the slot resolved to — never for the team (:625-716).
        const result = await slotHold.take({
            teamMemberId: memberId,
            date: picked.date,
            minute: picked.minute,
            duration: task.totalTime,
            latitude: location.latitude,
            longitude: location.longitude,
            ignoreTaskIds
        });
        setPending(null);

        const { minute } = picked;
        if (result.status === 'held') {
            setSelection(picked);
            setResolvedMemberId(isTeamCalendar ? memberId : null);
            // The route's open list is ONE member's day. On a team calendar the other members'
            // times are still good, so it must not grey anything out there.
            if (!isTeamCalendar && result.openMinutes && result.openMinutes.length > 0) {
                const open = Array.from(new Set([...result.openMinutes, minute]));
                setOpenByDate(current => ({ ...current, [picked.date]: open }));
            }
            return;
        }

        if (result.status === 'taken') {
            // Legacy `reserveCancel`: clear the pick, mark the slot, and redraw that day from the
            // open list the route sent back. (Not on a team calendar: another member may still be
            // free at that time, and the refreshed week says who.)
            if (!isTeamCalendar) {
                markRefused(picked.date, minute);
                if (result.openMinutes && result.openMinutes.length > 0) {
                    const open = result.openMinutes.filter(value => value !== minute);
                    setOpenByDate(current => ({ ...current, [picked.date]: open }));
                }
            }
            setMessage(HOLD_REFUSED_MESSAGE);
            void refreshAvailability();
            return;
        }

        void slotHold.release();
        setMessage(HOLD_FAILED_MESSAGE);
    };

    const buildRequest = (): RescheduleRequest | undefined => {
        const common = { source: 'Reschedule', teamAny: false };
        /*
         * ★ teamMemberId GOES ONLY WITH schedule AND preference (ROBO-616 Failed QA, 2026-10-03).
         *   It used to ride in `common`, and the route refuses it on tbd and cancel — 400 "This
         *   mode does not take shootDate, teamMemberId, …" (TaskScheduleService.Reschedule.cs,
         *   `takesTeamMember`). So TBD and Cancel could not be saved by anyone, guest, client or
         *   staff. The unit test pinned the refused body and the e2e spec mocks the route, so
         *   both were green.
         */
        // A preference brand has no chooser (the server answers `chooser: 'none'`): always the
        // task's current photographer.
        const photographer = task.teamMemberId !== null ? { teamMemberId: task.teamMemberId } : {};
        if (mode === 'tbd' || mode === 'cancel') {
            return { ...common, mode, ...(comment.trim() ? { comment: comment.trim() } : {}) };
        }
        if (mode === 'schedule' && slotCalendar) {
            // Legacy enables Save once there are `minSelections` picks (`reschedule.asp` :360).
            if (picks.length < requiredSelections(slotCalendar)) return undefined;
            return {
                ...common,
                ...photographer,
                mode: 'preference',
                // ★ Legacy's text, byte for byte, and NOT trimmed: it is what staff read on the
                //   order, and it must be the same whichever page the customer used.
                appointmentPreferences: formatPreferenceText(picks, slotCalendar.slots)
            };
        }
        if (mode === 'schedule' && hasPreferences) {
            const text = preferences.trim();
            return text
                ? { ...common, ...photographer, mode: 'preference', appointmentPreferences: text }
                : undefined;
        }
        if (mode === 'schedule' && selection) {
            // The chosen card's member, or — on a team calendar — the member the slot resolved to
            // (legacy `selectedPhotog`, `reschedule.asp` :372-377, :511).
            const memberId =
                target.kind === 'member'
                    ? target.memberId
                    : target.kind === 'team'
                      ? resolvedMemberId
                      : null;
            if (target.kind !== 'member' && memberId === null) return undefined;
            /*
             * ★ TRAVEL FEES ARE SENT BY STAFF ONLY. Legacy sent `nTF` / `nTFP` from the browser
             *   for everyone (`reschedule.asp` :483-512); here a client's or guest's fee is
             *   computed by the server and anything in the body is ignored, so the page does not
             *   pretend to state one. Staff state the chosen member's fee and payout, as legacy
             *   read them off the member's card — only when the team route listed that member.
             */
            const entry = members.find(member => member.id === memberId);
            const travel =
                task.callerKind === 'staff' && task.travelFeeEnabled && entry
                    ? { travelFee: entry.travelFee ?? 0, travelFeePay: entry.travelFeePay ?? 0 }
                    : {};
            return {
                ...common,
                // Legacy `sTeamAny`: '1' only when the Any Available card is the choice (:511).
                teamAny: target.kind === 'team' && target.isAny,
                ...(memberId !== null ? { teamMemberId: memberId } : {}),
                ...travel,
                mode: 'schedule',
                shootDate: toShootDate(selection.date, selection.minute),
                holdIds: slotHold.holdIds
            };
        }
        return undefined;
    };

    const request = buildRequest();
    const canSave = request !== undefined && !save.isPending && !pending;

    /** Legacy `reschedule.asp` :131-140: tell the mobile app what changed, then close the webview. */
    const notifyApp = async () => {
        const app = nativeWebView();
        if (!app) return;
        try {
            const summary = await fetchRescheduleAppointments(orderId, taskId, caller);
            app.postMessage(
                JSON.stringify({ update: { site: summary.siteId, appts: summary.appointments } })
            );
        } catch {
            // The save stands whether or not the summary could be read; the app still closes.
        }
        app.postMessage('close');
    };

    const handleSaved = (groups: RescheduleGroup[], sent: RescheduleRequest) => {
        const group = groups.find(row => row.taskIds?.includes(taskId)) ?? groups[0];
        if (sent.mode === 'schedule') {
            // The server cancels the caller's holds once the save succeeds.
            slotHold.forget();
        } else {
            void slotHold.release();
        }
        onCompleted({
            /*
             * A preference save REMOVES the appointment: proc_SetRescheduleTasks nulls dtShoot and
             * writes "the old appointment was removed" to the order notes. This used to keep
             * `task.shootDate`, so Update Complete showed a date the order no longer had (QA,
             * 2026-10-03). It shows what the server answered, as tbd and cancel do.
             */
            shootDate:
                sent.mode === 'preference'
                    ? (group?.shootDate ?? null)
                    : sent.mode === 'schedule'
                      ? (group?.shootDate ?? sent.shootDate ?? null)
                      : null,
            isDateOnly: sent.mode === 'schedule' && selection?.minute === null
        });
        void notifyApp();
    };

    const handleSaveFailed = (error: Error) => {
        const status = rescheduleErrorStatus(error);
        if (status === 409) {
            // Nothing was written. The time went between the pick and the save.
            if (selection && selection.minute !== null && target.kind === 'member') {
                markRefused(selection.date, selection.minute);
            }
            setSelection(null);
            setResolvedMemberId(null);
            void slotHold.release();
            void refreshAvailability();
            setMessage(CONFLICT_MESSAGE);
        } else if (status === 400 && error.message) {
            setMessage(error.message);
        } else if (status === 404) {
            setMessage(NOT_FOUND_MESSAGE);
        } else {
            setMessage(SAVE_FAILED_MESSAGE);
        }
    };

    const handleSave = () => {
        if (!request || !canSave) return;
        setMessage(null);
        // Legacy posts 'loading' to the app as the save starts (`reschedule.asp` :510).
        nativeWebView()?.postMessage('loading');
        save.mutate(request, {
            onSuccess: groups => handleSaved(groups, request),
            onError: handleSaveFailed
        });
    };

    const scheduleLabel = hasAppointment ? 'Reschedule Now' : 'Schedule Now';
    const modeCopy: Record<Mode, { label: string; hint: string }> = {
        schedule: {
            label: scheduleLabel,
            hint: slotCalendar
                ? 'Select the days and times that work for you below'
                : hasPreferences
                  ? 'Tell us which days and times work for you below'
                  : 'Select a new appointment time below'
        },
        tbd: { label: 'TBD', hint: "I need to reschedule, but I'm not sure when yet" },
        cancel: { label: 'Cancel', hint: 'I need to cancel this appointment' }
    };

    return (
        <Stack spacing={4}>
            <CurrentAppointment task={task} shootDate={task.shootDate} />

            {task.products.length > 0 && (
                <Box component="section" aria-labelledby={`${modeLabelId}-products`}>
                    <SectionHeading id={`${modeLabelId}-products`}>
                        {T('Products to be Rescheduled')}
                    </SectionHeading>
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                        {task.products.map((product, index) => (
                            <Typography component="li" variant="body1" key={`${product}-${index}`}>
                                {product}
                            </Typography>
                        ))}
                    </Box>
                </Box>
            )}

            {modes.length === 0 ? (
                <Alert severity="info">{T(CALL_TO_SCHEDULE)}</Alert>
            ) : (
                <FormControl component="fieldset" disabled={save.isPending}>
                    <FormLabel
                        component="legend"
                        id={modeLabelId}
                        sx={{ color: 'text.primary', fontSize: '1.25rem', fontWeight: 600, mb: 1 }}
                    >
                        {T(hasAppointment ? 'Change Appointment' : 'Set Appointment')}
                    </FormLabel>
                    <RadioGroup
                        aria-labelledby={modeLabelId}
                        name="reschedule-mode"
                        value={mode ?? ''}
                        onChange={event => {
                            setMode(event.target.value as Mode);
                            setMessage(null);
                        }}
                    >
                        {modes.map(option => (
                            <FormControlLabel
                                key={option}
                                value={option}
                                control={<Radio />}
                                sx={{ alignItems: 'flex-start', mb: 0.5 }}
                                label={
                                    <Box sx={{ pt: 1 }}>
                                        <Typography variant="body1">
                                            {T(modeCopy[option].label)}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {T(modeCopy[option].hint)}
                                        </Typography>
                                    </Box>
                                }
                            />
                        ))}
                    </RadioGroup>
                </FormControl>
            )}

            {mode === 'schedule' && slotCalendar && (
                <Box component="section" aria-labelledby={`${modeLabelId}-preferences`}>
                    <SectionHeading id={`${modeLabelId}-preferences`}>
                        {T('Appointment Preferences')}
                    </SectionHeading>
                    <PreferencePicker
                        {...slotCalendar}
                        value={picks}
                        onChange={setPicks}
                        label={T('Appointment Preferences')}
                        disabled={save.isPending}
                        weather={weather}
                    />
                </Box>
            )}

            {mode === 'schedule' && hasPreferences && !slotCalendar && (
                <Box component="section">
                    <TextField
                        label={T('Appointment Preferences')}
                        helperText={T(
                            'For example: weekday mornings, or Tuesday or Thursday after 2 PM.'
                        )}
                        value={preferences}
                        onChange={event => setPreferences(event.target.value)}
                        multiline
                        minRows={3}
                        fullWidth
                        disabled={save.isPending}
                        slotProps={{ htmlInput: { maxLength: 2000 } }}
                    />
                </Box>
            )}

            {mode === 'schedule' && hasCalendar && isChooserActive && chooser === 'cards' && (
                <Box component="section" aria-labelledby={`${modeLabelId}-photographers`}>
                    <Typography
                        id={`${modeLabelId}-photographers`}
                        ref={chooserHeadingRef}
                        tabIndex={-1}
                        component="h2"
                        variant="h6"
                        sx={{ mb: 1, outline: 'none' }}
                    >
                        {T('Choose a Photographer')}
                    </Typography>
                    <TeamPicker
                        members={members}
                        value={choice}
                        onChange={handleChoice}
                        anyAvailable={team?.anyAvailable ?? false}
                        displayLimit={team?.displayLimit}
                        isShowingAll={isShowingAll}
                        onShowAll={handleShowAll}
                        // The fee line is shown only when the ORDER's brand charges one on a
                        // reschedule (legacy read the caller's brand — spec §5.1, quirk 18).
                        showTravelFees={task.travelFeeEnabled}
                        // The module does not know how this app stores photos: the page builds
                        // the URL from the member's `media`, as the team-members grid does.
                        photoUrl={card =>
                            teamPhotoUrl(members.find(member => member.id === card.id)?.media)
                        }
                        label={T('Choose a Photographer')}
                        disabled={save.isPending || Boolean(pending)}
                    />
                </Box>
            )}

            {mode === 'schedule' && hasCalendar && target.kind !== 'empty' && (
                <Box component="section" aria-labelledby={`${modeLabelId}-times`}>
                    <Typography
                        id={`${modeLabelId}-times`}
                        // The hidden-team calendar has no cards to land on: focus comes here.
                        ref={chooser === 'team-calendar' ? chooserHeadingRef : undefined}
                        tabIndex={chooser === 'team-calendar' ? -1 : undefined}
                        component="h2"
                        variant="h6"
                        sx={{ mb: 1, outline: 'none' }}
                    >
                        {target.kind === 'member' && target.name
                            ? `${T('Available Times')} — ${target.name}`
                            : target.kind === 'team' && target.isAny
                              ? `${T('Available Times')} — ${T(ANY_AVAILABLE_LABEL)}`
                              : T('Available Times')}
                    </Typography>
                    {isTeamPending ? (
                        // Whose calendar this is, and which weekdays a service area closes, is the
                        // team route's to say: wait for it rather than draw a week twice.
                        <Stack role="status" alignItems="center" spacing={1} sx={{ py: 4 }}>
                            <CircularProgress size={28} />
                            <Typography variant="body2">{T('Checking availability...')}</Typography>
                        </Stack>
                    ) : target.kind === 'none' ? (
                        <Alert severity="info">{T(CALL_TO_SCHEDULE)}</Alert>
                    ) : target.kind === 'choose' ? (
                        <Alert severity="info" data-testid="choose-photographer">
                            {T(CHOOSE_PHOTOGRAPHER_MESSAGE)}
                        </Alert>
                    ) : (
                        <>
                            <AvailabilityPicker
                                // A different calendar starts again from this week.
                                key={calendarKey}
                                brandId={task.brandId}
                                {...(target.kind === 'team'
                                    ? {
                                          teamMemberIds: target.memberIds,
                                          team: team?.teamMode,
                                          favoriteIds
                                      }
                                    : { teamMemberId: target.memberId })}
                                weekMask={weekMask}
                                duration={task.totalTime}
                                location={location}
                                ignoreTaskIds={ignoreTaskIds}
                                ignoreReservationIds={slotHold.holdIds}
                                downloadGuid={caller.downloadGuid}
                                today={brandToday(hostInfo?.timezoneOffset)}
                                value={selection}
                                pending={pending}
                                onSelect={picked => void handleSelect(picked)}
                                isSlotDisabled={isSlotDisabled}
                                disabled={save.isPending}
                                weather={weather}
                            />
                            {slotHold.hold && selection && (
                                <HoldCountdown expiresAt={slotHold.hold.expiresAt} />
                            )}
                        </>
                    )}
                    {task.canChoosePhotographer && teamQuery.isError && (
                        <Alert
                            severity="warning"
                            sx={{ mt: 2 }}
                            action={
                                <Button
                                    color="inherit"
                                    size="small"
                                    onClick={() => void teamQuery.refetch()}
                                >
                                    {T('Try again')}
                                </Button>
                            }
                        >
                            {T(TEAM_FAILED_MESSAGE)}
                        </Alert>
                    )}
                    {chooser !== 'none' && !isChooserActive && (
                        <Box sx={{ textAlign: 'center', mt: 2 }}>
                            <Button
                                variant="outlined"
                                onClick={handleOpenChooser}
                                disabled={save.isPending || Boolean(pending)}
                            >
                                {T(CHECK_MORE_LABEL)}
                            </Button>
                        </Box>
                    )}
                </Box>
            )}

            {(mode === 'tbd' || mode === 'cancel') && (
                <Box component="section">
                    <TextField
                        label={T('Additional Information')}
                        value={comment}
                        onChange={event => setComment(event.target.value)}
                        multiline
                        minRows={3}
                        fullWidth
                        disabled={save.isPending}
                    />
                </Box>
            )}

            <DroneAirspaceNotice notice={droneNoticeValue} />

            {message && (
                <Alert severity="error" role="alert" data-testid="reschedule-message">
                    {T(message)}
                </Alert>
            )}

            {modes.length > 0 && (
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    justifyContent="center"
                    sx={{ '& > button': { minWidth: { sm: 220 } } }}
                >
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={!canSave}
                        loading={save.isPending}
                    >
                        {T('Save Changes')}
                    </Button>
                    {cancelButton}
                </Stack>
            )}
        </Stack>
    );
}

/**
 * The page frame: the brand's mark, the page title, and one column of content.
 *
 * ★ THE OUTER BOX IS THE SCROLLPORT, BECAUSE NOTHING ABOVE IT SCROLLS. The hub shell sets
 * `overflow: hidden` on `html` and `body` (`App.css`) so its full-viewport layouts own their
 * scrolling. This page is taller than a screen (appointment, products, modes, a week of slots), and
 * without its own scroll container everything below the fold - Save included - was unreachable
 * (2026-10-03). Same fix as `hub-mcp-connect-page`: the outer box is exactly one viewport tall and
 * scrolls; the inner one keeps the layout with `min-height: 100%`.
 */
function RescheduleFrame({ children }: { children: ReactNode }) {
    const { data: hostInfo } = useHost();
    const logoUrl = hostInfo?.logo ? getMediaSize(hostInfo.logo, { width: 600 })?.url : null;

    return (
        <Box data-testid="reschedule-frame" sx={{ height: '100dvh', overflowY: 'auto' }}>
            <Box
                component="main"
                sx={{
                    minHeight: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    px: 2,
                    py: { xs: 2, sm: 4 }
                }}
            >
                <Paper sx={{ width: '100%', maxWidth: 860, p: { xs: 2, sm: 4 } }}>
                    <Stack spacing={3}>
                        {logoUrl && (
                            <Box
                                component="img"
                                src={logoUrl}
                                alt={hostInfo?.brandName ?? ''}
                                sx={{ maxWidth: 180, maxHeight: 64, objectFit: 'contain' }}
                            />
                        )}
                        <Typography component="h1" variant="h5">
                            {T('Reschedule Appointment')}
                        </Typography>
                        {children}
                    </Stack>
                </Paper>
            </Box>
        </Box>
    );
}

function SectionHeading({ id, children }: { id?: string; children: ReactNode }) {
    return (
        <Typography id={id} component="h2" variant="h6" sx={{ mb: 1 }}>
            {children}
        </Typography>
    );
}

interface CurrentAppointmentProps {
    task: RescheduleTask;
    shootDate: string | null;
    isDateOnly?: boolean;
}

/** Legacy's "Current Appointment" table (`reschedule.asp` :549-576), in both page states. */
function CurrentAppointment({ task, shootDate, isDateOnly }: CurrentAppointmentProps) {
    const headingId = useId();
    const address = formatSiteAddress(task.site);
    const date = formatAppointmentDate(shootDate);
    const time = formatAppointmentTime(shootDate);

    const rows: [string, string][] = [];
    if (address) rows.push(['Location', address]);
    // No date reads "To Be Determined" whatever the reason — unscheduled, TBD or just cancelled —
    // as legacy's re-read of the task does after a save.
    if (date) {
        rows.push(['Date', date]);
        if (time && !isDateOnly) rows.push(['Time', time]);
    } else {
        rows.push(['Date/Time', T('To Be Determined')]);
    }

    return (
        <Box component="section" aria-labelledby={headingId}>
            <SectionHeading id={headingId}>{T('Current Appointment')}</SectionHeading>
            <Box
                component="dl"
                sx={{
                    m: 0,
                    display: 'grid',
                    gridTemplateColumns: 'max-content 1fr',
                    columnGap: 2,
                    rowGap: 0.5
                }}
            >
                {rows.map(([label, value]) => (
                    <Box key={label} sx={{ display: 'contents' }}>
                        <Typography component="dt" variant="body2" color="text.secondary">
                            {T(label)}
                        </Typography>
                        <Typography component="dd" variant="body1" sx={{ m: 0 }}>
                            {value}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

interface StateMessageProps {
    title: string;
    body: string;
    severity: 'success' | 'info' | 'warning';
    /** Move focus here when it appears — the form the user was in has just been replaced. */
    shouldFocus?: boolean;
}

function StateMessage({ title, body, severity, shouldFocus }: StateMessageProps) {
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (shouldFocus) ref.current?.focus();
    }, [shouldFocus]);

    return (
        <Alert ref={ref} tabIndex={-1} severity={severity} role="status" sx={{ outline: 'none' }}>
            <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 600 }}>
                {T(title)}
            </Typography>
            {T(body)}
        </Alert>
    );
}

/** Legacy's "Appointment Time Held For m:ss" (`cart-gcal.js` :454-468, :670). */
function HoldCountdown({ expiresAt }: { expiresAt: number }) {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, []);

    return (
        // A timer is announced on request, not every second.
        <Typography
            role="timer"
            aria-live="off"
            variant="body2"
            sx={{ mt: 1.5, textAlign: 'center' }}
            data-testid="hold-countdown"
        >
            {T('Appointment Time Held For')} {formatCountdown(expiresAt - now)}
        </Typography>
    );
}
